/* =========================================================================
   Shopping lists — single recipe and combined multi-recipe.
   Merge logic ported from iteration 1 unchanged.
   ========================================================================= */

import { openModal, closeModal, toast, registerActions } from './dom.js'
import { esc, scaledIngredients, ingLine } from '../data/model.js'
import { byId } from '../data/store.js'
import { state, currentServes } from '../app.js'

let payload = { title: '', txt: '' }

function listTextFor(r, serves) {
  return scaledIngredients(r, serves).map(i => '• ' + ingLine(i)).join('\n')
}

function showShopModal(title, txt) {
  payload = { title, txt }
  const mailto = 'mailto:?subject=' + encodeURIComponent(title) + '&body=' + encodeURIComponent(txt)
  openModal(`
    <h3>${esc(title)}</h3>
    <div class="shoplist">${esc(txt)}</div>
    <div class="actions">
      <button class="btn" data-act="copyShop">📋 Copy</button>
      <a class="btn" href="${esc(mailto)}" data-act="noop" data-passthrough="1">✉️ Email to me</a>
      ${navigator.share ? '<button class="btn" data-act="shareShop">􀈂 Share</button>' : ''}
      <button class="btn primary" data-act="closeModal">Done</button>
    </div>`)
}

function singleShoppingList(id) {
  const r = byId(id)
  if (!r) return
  const serves = currentServes(r)
  showShopModal(`Shopping list — ${r.title} (serves ${serves})`, listTextFor(r, serves))
}

export function mergedShoppingList() {
  const chosen = [...state.selected].map(byId).filter(Boolean)
  if (!chosen.length) { toast('Tick some recipes first'); return }

  // merge identical item+unit, list anything unquantified separately
  const map = new Map()
  const extras = []
  for (const r of chosen) {
    const serves = currentServes(r)
    for (const i of scaledIngredients(r, serves)) {
      const key = i.item.toLowerCase().trim() + '|' + (i.unit || '').toLowerCase()
      if (i.sq == null) { extras.push(i.item + `  (${r.title})`); continue }
      if (map.has(key)) map.get(key).sq += i.sq
      else map.set(key, { ...i })
    }
  }

  let txt = [...map.values()].map(i => '• ' + ingLine(i)).join('\n')
  if (extras.length) txt += '\n\nTo taste / check pantry:\n' + extras.map(e => '• ' + e).join('\n')
  txt += '\n\nRecipes: ' + chosen.map(r => `${r.title} (serves ${currentServes(r)})`).join('; ')

  showShopModal(`Combined shopping list — ${chosen.length} recipes`, txt)
}

registerActions({
  noop: () => {},
  shopSingle: d => singleShoppingList(d.id),
  shopMerged: mergedShoppingList,

  copyShop: async () => {
    const text = payload.title + '\n\n' + payload.txt
    try {
      await navigator.clipboard.writeText(text)
      toast('Copied to clipboard')
    } catch {
      // Safari refuses clipboard writes outside a trusted gesture in some contexts
      toast('Copy failed — select the list and copy manually')
    }
  },

  // [iOS] the native share sheet is the natural way to send a list from a phone
  shareShop: async () => {
    try {
      await navigator.share({ title: payload.title, text: payload.txt })
    } catch { /* user dismissed */ }
  },
})
